/**
 * Lädt das Thumbnail einer Markup-Szene als Object-URL.
 *
 * Der Thumbnail-Endpoint verlangt einen Bearer-Token — ein `<img src="/api/…">`
 * sendet den nicht und lief deshalb in 401 (die Galerie zeigte dauerhaft
 * „Kein Thumbnail", obwohl eins existierte). Wir holen die Bytes darum per
 * authentifiziertem fetch und geben eine Object-URL zurück, die beim Unmount
 * wieder freigegeben wird.
 */
import { useEffect, useState } from 'react';
import { authHeader } from '@/lib/api';
import { thumbnailUrl } from './api';

export interface SceneThumbnailState {
  /** Object-URL für `<img src>` — null solange nicht geladen oder fehlgeschlagen. */
  src: string | null;
  loading: boolean;
  failed: boolean;
}

export function useSceneThumbnail(sceneId: number, enabled: boolean): SceneThumbnailState {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setSrc(null);
      setLoading(false);
      setFailed(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setFailed(false);

    (async () => {
      try {
        const resp = await fetch(thumbnailUrl(sceneId), {
          credentials: 'include',
          headers: authHeader(),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        setLoading(false);
      } catch {
        if (cancelled) return;
        setFailed(true);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sceneId, enabled]);

  return { src, loading, failed };
}
